import { accountStore } from '@stores/bootstrap-stores/account';
import { collectionRef, Table, TableForm, TableItem } from '@vincerosoft/assessfluence-types';
import { apiService, QueryConstraintModified } from 'src/backend-api/default.api';

// Tables the caller may see, per the rules in README.md. Own tables come first.
// Optionally restricted to a set of form ids.
export async function fetchVisibleTables(formIds?: string[]) {
  const { scope, account, docScope } = accountStore.getState();

  const fetchTables = async (filter: QueryConstraintModified) => {
    if (!formIds) {
      return apiService.getDocs<Table>(collectionRef.tables, filter);
    }

    const chunks = await apiService.getDocsInChunks<Table>(
      collectionRef.tables,
      { field: 'form_id', operator: '$in', values: formIds },
      25,
      filter
    );

    return chunks.flat();
  };

  if (docScope.domain !== 'ENTITY') {
    return fetchTables(docScope);
  }

  const ownTables = await fetchTables(docScope);

  // Standalone entities see tenant tables, linked entities see their parent group's tables
  const parentTables = await fetchTables(
    scope === 'linked'
      ? {
          domain: 'GROUP_ENTITY',
          group_entity_id: account?.group_entity_id
        }
      : { domain: 'TENANT' }
  );

  // Visible when the entity may add items, or when at least one item is shared with it
  const visibleParentTables = (
    await Promise.all(
      parentTables.map(async (table) => {
        if (table.entities_can_add_items) {
          return table;
        }
        const shared = await apiService.getDocs<TableItem>(
          table.table_name,
          { shared_entity_ids: { $includes: account.id } },
          { limit: 1 }
        );
        return shared.length ? table : null;
      })
    )
  ).filter((table): table is Table => !!table);

  return [...ownTables, ...visibleParentTables];
}

export async function getTables(forms: TableForm[]) {
  const tablesByFormId = new Map<string, Table>();

  if (!forms?.length) {
    return tablesByFormId;
  }

  const tables = await fetchVisibleTables(forms.map((form) => form.id));

  for (const table of tables) {
    if (!tablesByFormId.has(table.form_id)) {
      tablesByFormId.set(table.form_id, table);
    }
  }

  return tablesByFormId;
}
